import React, { useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSite } from '../content/SiteContext';
import { sendToWhatsApp, formatMessage, isValidUaeMobile, track } from '../lib/contact';
import { PageMeta, Icon, Field, describedBy, Price, StoreBadge } from '../components/ui';

const VEHICLES = ['Sedan / hatchback', 'SUV / 4x4', 'Van / pickup / other'];
const PARKING = ['Villa / house', 'Building car park', 'Office car park', 'Other'];
const WINDOWS = ['Morning', 'Afternoon', 'Evening'];

const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const Book = () => {
  const { PACKAGES, ADDONS, CITIES, BUSINESS, getPackage } = useSite();
  const [params] = useSearchParams();
  const initialPkg = getPackage(params.get('package')) ? params.get('package') : 'deluxe';
  const initialCity = CITIES.some((c) => c.id === params.get('city')) ? params.get('city') : '';

  const [form, setForm] = useState({
    vehicle: VEHICLES[0], pkg: initialPkg, addons: [], city: initialCity, area: '', parking: '',
    date: '', window: '', name: '', phone: '', notes: ''
  });
  const [errors, setErrors] = useState({});
  const [sentUrl, setSentUrl] = useState(null);
  const started = useRef(false);

  const pkg = getPackage(form.pkg);
  const chosenAddons = ADDONS.filter((a) => form.addons.includes(a.id));
  const total = pkg.price + chosenAddons.reduce((s, a) => s + a.price, 0);

  const markStarted = () => {
    if (!started.current) {
      started.current = true;
      track('begin_checkout', { package: form.pkg });
    }
  };

  const update = (name, value) => {
    markStarted();
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };
  const onChange = (e) => update(e.target.name, e.target.value);
  const toggleAddon = (id) => update('addons', form.addons.includes(id) ? form.addons.filter((a) => a !== id) : [...form.addons, id]);

  const validate = () => {
    const e = {};
    if (!form.city) e.city = 'Choose Abu Dhabi or Dubai.';
    if (!form.area.trim()) e.area = 'Enter your area, community or building.';
    if (!form.parking) e.parking = 'Tell us where the car is parked.';
    if (!form.date) e.date = 'Choose a preferred date.';
    else if (form.date < todayISO()) e.date = 'Choose today or a future date.';
    if (!form.window) e.window = 'Choose a preferred time of day.';
    if (!form.name.trim()) e.name = 'Enter your name.';
    if (!isValidUaeMobile(form.phone)) e.phone = 'Enter a UAE mobile number, for example 050 123 4567.';
    return e;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (sentUrl) return;
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      const first = Object.keys(e)[0];
      document.getElementById(first === 'window' ? 'window-0' : first)?.focus();
      return;
    }
    const cityName = CITIES.find((c) => c.id === form.city).name;
    const text = formatMessage('Hi Quick Way, I would like to book a car wash.', [
      ['Package', `${pkg.name} (AED ${pkg.price})`],
      ['Extras', chosenAddons.map((a) => `${a.name} (AED ${a.price})`).join(', ')],
      ['Estimated total', `AED ${total}`],
      ['Vehicle', form.vehicle],
      ['Emirate', cityName],
      ['Area / building', form.area],
      ['Parking', form.parking],
      ['Preferred date', form.date],
      ['Preferred time', form.window],
      ['Name', form.name],
      ['Mobile', form.phone],
      ['Notes', form.notes]
    ]);
    setSentUrl(sendToWhatsApp(text, 'generate_lead', { lead_type: 'booking', package: form.pkg, emirate: form.city, value: total, currency: 'AED' }));
  };

  if (sentUrl) {
    return (
      <>
        <PageMeta title="Book a Mobile Car Wash | Quick Way" description="Book a mobile car wash in Abu Dhabi or Dubai." path="/book" noindex />
        <section className="section book-done">
          <div className="container container--narrow form-success" role="status">
            <Icon name="chat" size={40} />
            <h1>Press send in WhatsApp to request your booking</h1>
            <p>
              Your booking details are ready in WhatsApp. Your request reaches us once you press <strong>send</strong>.
              Your booking is <strong>not confirmed yet</strong>. Our team will reply on WhatsApp to confirm the time slot and the final price.
            </p>
            <div className="btn-row btn-row--center">
              <a href={sentUrl} target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp btn-lg">Open WhatsApp again</a>
              <button type="button" className="btn btn-outline-dark btn-lg" onClick={() => setSentUrl(null)}>Edit booking details</button>
            </div>
            <div className="form-success__app">
              <p className="muted">Tip: next time order in the Quick Way app and manage your washes in one place.</p>
              <StoreBadge source="book_success" />
            </div>
            <p className="muted">WhatsApp not working? Call us on <a href={`tel:${BUSINESS.phone}`}>{BUSINESS.phoneDisplay}</a>.</p>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageMeta
        title="Book a Mobile Car Wash in Abu Dhabi or Dubai | Quick Way"
        description="Order a doorstep car wash in Abu Dhabi or Dubai in the Quick Way Android app — or send your booking on WhatsApp. Packages from AED 30."
        path="/book"
        crumbs={[{ name: 'Book a wash', path: '/book' }]}
      />
      <section className="book">
        <div className="container">
          <header className="book__head">
            <h1>Book a car wash</h1>
            <p>The fastest way is the Quick Way app: pin your location, pick a package and manage every wash in one place.</p>
          </header>

          <div className="app-order-panel">
            <div>
              <h2>Order in the Quick Way app</h2>
              <p>Pick your car's location on a map, choose a package and extras, and track your bookings.</p>
              <StoreBadge source="book_page" />
              <p className="muted">Android only — iPhone? Use the WhatsApp booking below.</p>
            </div>
            <img className="app-order-panel__shot" src="/img/app-location.jpg" alt="Quick Way app: choosing your car wash location on a map" width="385" height="816" loading="lazy" />
          </div>

          <header className="book__head">
            <h2 className="h-section">Book on WhatsApp instead</h2>
            <p>Fill in the details below. We will open WhatsApp with your booking ready to send, then confirm your slot and final price.</p>
          </header>

          <form className="book__layout" onSubmit={onSubmit} noValidate>
            <div className="book__steps">
              <fieldset className="book-step">
                <legend><span className="book-step__num">1</span> Your vehicle</legend>
                <div className="choice-row">
                  {VEHICLES.map((v) => (
                    <label key={v} className={`chip${form.vehicle === v ? ' is-selected' : ''}`}>
                      <input type="radio" name="vehicle" value={v} checked={form.vehicle === v} onChange={onChange} />
                      {v}
                    </label>
                  ))}
                </div>
                {form.vehicle !== VEHICLES[0] && (
                  <p className="field__hint">For larger vehicles we confirm the final price before the wash.</p>
                )}
              </fieldset>

              <fieldset className="book-step">
                <legend><span className="book-step__num">2</span> Package</legend>
                <div className="choice-cards">
                  {PACKAGES.map((p) => (
                    <label key={p.id} className={`choice-card${form.pkg === p.id ? ' is-selected' : ''}`}>
                      <input type="radio" name="pkg" value={p.id} checked={form.pkg === p.id} onChange={onChange} />
                      <span className="choice-card__top">
                        <strong>{p.name}</strong>
                        <Price value={p.price} />
                      </span>
                      <span className="choice-card__desc">{p.summary}</span>
                      <span className="choice-card__meta"><Icon name="clock" size={14} /> {p.duration}</span>
                    </label>
                  ))}
                </div>
                <p className="field__hint"><Link to="/pricing">Compare what each package includes</Link></p>
              </fieldset>

              <fieldset className="book-step">
                <legend><span className="book-step__num">3</span> Extras <span className="muted">(optional)</span></legend>
                <div className="choice-cards choice-cards--compact">
                  {ADDONS.map((a) => (
                    <label key={a.id} className={`choice-card${form.addons.includes(a.id) ? ' is-selected' : ''}`}>
                      <input type="checkbox" checked={form.addons.includes(a.id)} onChange={() => toggleAddon(a.id)} />
                      <span className="choice-card__top"><strong>{a.name}</strong><span>+ AED {a.price}</span></span>
                      <span className="choice-card__desc">{a.desc}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset className="book-step">
                <legend><span className="book-step__num">4</span> Location</legend>
                <div className="form-grid">
                  <Field label="Emirate" id="city" error={errors.city} required>
                    <select id="city" name="city" value={form.city} onChange={onChange} aria-invalid={!!errors.city} aria-describedby={describedBy('city', errors.city)}>
                      <option value="">Select</option>
                      {CITIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Where is the car parked?" id="parking" error={errors.parking} required>
                    <select id="parking" name="parking" value={form.parking} onChange={onChange} aria-invalid={!!errors.parking} aria-describedby={describedBy('parking', errors.parking)}>
                      <option value="">Select</option>
                      {PARKING.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Area, community or building" id="area" error={errors.area} required
                  hint="For buildings, include the building name, parking level and bay if you know them.">
                  <input id="area" name="area" value={form.area} onChange={onChange} autoComplete="street-address"
                    placeholder="e.g. Al Reem Island, Sky Tower, P2 bay 145" aria-invalid={!!errors.area}
                    aria-describedby={describedBy('area', errors.area, true)} />
                </Field>
                <p className="field__hint">We serve Abu Dhabi and Dubai only. <Link to="/locations">See service areas</Link>.</p>
              </fieldset>

              <fieldset className="book-step">
                <legend><span className="book-step__num">5</span> Preferred day and time</legend>
                <Field label="Preferred date" id="date" error={errors.date} required>
                  <input id="date" name="date" type="date" min={todayISO()} value={form.date} onChange={onChange}
                    aria-invalid={!!errors.date} aria-describedby={describedBy('date', errors.date)} />
                </Field>
                <div className={`field${errors.window ? ' field--error' : ''}`}>
                  <span className="field__label" id="window-label">Preferred time of day<span className="req" aria-hidden="true"> *</span></span>
                  <div className="choice-row" role="radiogroup" aria-labelledby="window-label" aria-describedby={errors.window ? 'window-error' : 'window-hint'}>
                    {WINDOWS.map((w, i) => (
                      <label key={w} className={`chip${form.window === w ? ' is-selected' : ''}`}>
                        <input id={`window-${i}`} type="radio" name="window" value={w} checked={form.window === w} onChange={onChange} />
                        {w}
                      </label>
                    ))}
                  </div>
                  {errors.window
                    ? <p className="field__error" id="window-error" role="alert">{errors.window}</p>
                    : <p className="field__hint" id="window-hint">This is a preference. We confirm the exact time slot on WhatsApp.</p>}
                </div>
              </fieldset>

              <fieldset className="book-step">
                <legend><span className="book-step__num">6</span> Your details</legend>
                <div className="form-grid">
                  <Field label="Name" id="name" error={errors.name} required>
                    <input id="name" name="name" value={form.name} onChange={onChange} autoComplete="name"
                      aria-invalid={!!errors.name} aria-describedby={describedBy('name', errors.name)} />
                  </Field>
                  <Field label="Mobile number" id="phone" error={errors.phone} required>
                    <input id="phone" name="phone" type="tel" inputMode="tel" value={form.phone} onChange={onChange}
                      autoComplete="tel" placeholder="050 123 4567" aria-invalid={!!errors.phone} aria-describedby={describedBy('phone', errors.phone)} />
                  </Field>
                </div>
                <Field label="Notes" id="notes" hint="Car make and colour, gate code, building access steps, etc.">
                  <textarea id="notes" name="notes" rows="3" value={form.notes} onChange={onChange} aria-describedby="notes-hint" />
                </Field>
              </fieldset>
            </div>

            <aside className="book__summary" aria-label="Booking summary">
              <div className="summary-card">
                <h2>Your booking</h2>
                <dl>
                  <div><dt>{pkg.name}</dt><dd>AED {pkg.price}</dd></div>
                  {chosenAddons.map((a) => <div key={a.id}><dt>{a.name}</dt><dd>AED {a.price}</dd></div>)}
                  <div className="summary-card__total"><dt>Estimated total</dt><dd>AED {total}</dd></div>
                </dl>
                <p className="summary-card__note">Final price, including any applicable VAT, is confirmed by our team before the wash.</p>
                <button type="submit" className="btn btn-whatsapp btn-block btn-lg">Send booking on WhatsApp</button>
                <p className="summary-card__note">Prefer the app? <a href={BUSINESS.playStore} target="_blank" rel="noopener noreferrer">Book on Android</a></p>
              </div>
            </aside>
          </form>
        </div>
      </section>
    </>
  );
};

export default Book;
